import React, { createContext, useContext, useEffect, useState } from 'react';
import { Session, User } from '@supabase/supabase-js';
import { supabase } from '../lib/supabaseBrowser';

type AuthContextType = {
  user: User | null;
  session: Session | null;
  isLoading: boolean;
  signOut: () => Promise<void>;
};

const AuthContext = createContext<AuthContextType>({
  user: null,
  session: null,
  isLoading: true,
  signOut: async () => {},
});

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    const initAuth = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (mounted) {
          setSession(session);
          setUser(session?.user ?? null);
        }
      } catch (error) {
        console.error('Error getting session:', error);
      } finally {
        if (mounted) {
          setIsLoading(false);
        }
      }
    };

    // Defer auth initialization to requestIdleCallback to avoid blocking initial render
    let subscriptionCleanup: (() => void) | null = null;

    const startAuthInit = async () => {
      try {
        // First, set up the auth state listener
        const {
          data: { subscription },
        } = supabase.auth.onAuthStateChange((_event, session) => {
          if (mounted) {
            setSession(session);
            setUser(session?.user ?? null);
            setIsLoading(false);
          }
        });

        subscriptionCleanup = () => subscription?.unsubscribe();

        // Then, check current session
        await initAuth();
      } catch (error) {
        console.error('Error initializing auth:', error);
        if (mounted) setIsLoading(false);
      }
    };

    // Use requestIdleCallback to defer auth setup without blocking render
    if ('requestIdleCallback' in window) {
      requestIdleCallback(() => {
        if (mounted) startAuthInit();
      }, { timeout: 2000 }); // Timeout after 2s to ensure auth loads
    } else {
      // Fallback for browsers without requestIdleCallback
      setTimeout(() => {
        if (mounted) startAuthInit();
      }, 0); // Use setTimeout(..., 0) for immediate but non-blocking execution
    }

    return () => {
      mounted = false;
      subscriptionCleanup?.();
    };
  }, []);

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  return (
    <AuthContext.Provider value={{ user, session, isLoading, signOut }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  return useContext(AuthContext);
};
